import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { isDev, isProductionHost } from 'lib/env'
import { isFunction } from 'lodash'

import ScrollToError from './ScrollToError'
import { Formik } from 'formik'
import { FormikDebug } from 'components/Util/FormikDebug'
import ExtendedFormikPrompt from './ExtendedFormikPrompt'
import { logException } from 'lib/errors'

export const ExtendedFormikContext = React.createContext()

export class Validator {
    validator
    message

    constructor (validator, message) {
        this.validator = validator
        this.message = message
    }

    static create (validator, message) {
        return new Validator(validator, message)
    }
}

export class Manipulator {
    manipulator

    constructor (manipulator) {
        this.manipulator = manipulator
    }

    static create (manipulator) {
        return new Manipulator(manipulator)
    }
}

export const findFormErrors = (rootElement) => {
    const emptyClass = '.form-element--empty.form-element--required'
    const invalidClass = '.form-element--invalid'
    const invalidElements = rootElement.querySelectorAll(`${emptyClass}, ${invalidClass}`)

    return {
        dataMissing: invalidElements.length > 0,
        elements: invalidElements
    }
}

class ExtendedFormik extends React.Component {
    mounted = false
    asyncSetState (...args) {
        // avoid setting state after unmount in case async operations attempts to do so
        if (this.mounted) this.setState(...args)
    }

    constructor (props) {
        super(props)

        this.state = {
            fieldsValidating: {},
            debugVisible: props.debug && isDev(),
            renderForm: !this.props.initValidation,
            initValidationDone: false,
        }
    }

    componentDidMount () {
        this.mounted = true

        const {
            initValidation,
            validationSchema,
            initialValues,
            initValidationValues,
            initValidationOnlyTouchFilled,
            disableInitValidationOnFormikReinitialize,
        } = this.props

        const { initValidationDone } = this.state

        if (initValidation && (!initValidationDone || !disableInitValidationOnFormikReinitialize)) {
            const actions = { setRenderForm: this.setRenderForm }

            const validationValues = initValidationValues || initialValues

            validationSchema.validate({ ...validationValues }, { abortEarly: false })
                .then((result) => initValidation(true, result, actions))
                .catch((result) => {
                    // Set initial validation errors and touch the fields to make the errors visible right away
                    const validationErrorState = result.inner.reduce((result, value) => {
                        if (!initValidationOnlyTouchFilled || validationValues[value.path]) {
                            result.initialTouched[value.path] = 'true'
                        }
                        result.initialErrors[value.path] = value.errors
                        return result
                    }, {
                        initialTouched: {},
                        initialErrors: {},
                    })

                    this.asyncSetState({
                        initValidationDone: true,
                        ...validationErrorState,
                    })

                    initValidation(false, result, actions)
                })
        }

        this.handleAutoFocus()
    }

    componentWillUnmount () {
        this.mounted = false
    }

    handleAutoFocus = () => {
        const { autoFocusToField, autoFocusFirst } = this.props

        if (autoFocusToField && autoFocusToField !== '') {
            this.focusField(autoFocusToField)
        } else if (autoFocusFirst) {
            this.focusFirst()
        }
    }

    toggleDebugView = () => {
        this.asyncSetState({ debugVisible: !this.state.debugVisible })
    }

    setRenderForm = (value) => {
        this.setState({ renderForm: value })
    }

    setFieldValidating = (name, validating) => {
        const fieldsValidating = this.state.fieldsValidating
        if (fieldsValidating[name] !== validating) {
            fieldsValidating[name] = validating
            this.asyncSetState({ fieldsValidating })
        }
    }

    validateFieldAgainstSchema = (name, value) => {
        const { validationSchema } = this.props

        const valuesToValidate = {}
        valuesToValidate[name] = value

        let errorMessage

        try {
            validationSchema.validateSyncAt(name, valuesToValidate)
        } catch (error) {
            errorMessage = error.errors[0]
        }

        return errorMessage
    }

    validateAsync = async (validators, value) => {
        let error

        for (const validator of validators) {
            const action = validator.validator
            const isValid = await action(value)
            if (!isValid) {
                error = validator.message
                break
            }
        }

        return error
    }

    validateField = (name, validators) => {
        validators = Array.isArray(validators) ? validators : [validators]
        return async (value) => {
            const schemaError = this.validateFieldAgainstSchema(name, value)
            if (schemaError) return schemaError

            this.setFieldValidating(name, true)

            const error = await this.validateAsync(validators, value)

            this.setFieldValidating(name, false)

            if (error) {
                return error
            }

            return undefined
        }
    }

    isFieldRequired = (fieldName, values) => {
        const recurSchema = (fields, schema) => {
            if (!schema) {
                return null
            }
            if (fields.length === 1) {
                return schema.fields[fields[0]]
            }
            const name = fields.pop()

            return recurSchema(fields, schema.fields[name])
        }
        const fieldSchema = recurSchema(fieldName.split('.').reverse(), this.props.validationSchema)

        if (!fieldSchema) {
            logException(new Error(`Missing validation schema for field "${fieldName}". Formik form id: "${this.props.id}"`))
            return false
        }

        return this.isRequiredSchema(fieldSchema, values)
    }

    isRequiredSchema = (schema, values) => {
        if (schema?.exclusiveTests?.required) {
            return true
        }

        if (schema.conditions) {
            for (let i = 0; i < schema.conditions.length; i++) {
                const condition = schema.conditions[i]
                const depFieldValue = isFunction(condition.refs[0].getter)
                    ? condition.refs[0].getter(values)
                    : ''

                const conditionSchemas = condition.fn(depFieldValue, [], depFieldValue)
                if (!conditionSchemas) continue

                const required = conditionSchemas.find((schema) => (
                    this.isRequiredSchema(schema, values)
                ))

                if (required) return true
            }
        }

        return false
    }

    scrollToError = () => {
        const errors = findFormErrors(document.querySelector(`#${this.props.id}`))
        if (errors.dataMissing) {
            const firstErroredElement = errors.elements[0]

            try {
                firstErroredElement.scrollIntoView({ block: 'center', behavior: 'smooth' })
            } catch (e) {
                firstErroredElement.scrollIntoView()
            }
        }
    }

    manipulateField = (setFieldValue, name, manipulators, callback = null) => {
        manipulators = Array.isArray(manipulators) ? manipulators : [manipulators]
        return async (e) => {
            let manipulatedValue = e.target.value
            for (const manipulator of manipulators) {
                manipulatedValue = manipulator.manipulator(manipulatedValue, e)
            }

            setFieldValue(name, manipulatedValue)

            if (callback) callback(e, name, manipulatedValue)
        }
    }

    focusFirst = () => {
        const { id } = this.props

        const selector = `#${id} input, #${id} textarea, #${id} select`
        const targetElement = document.querySelector(selector)

        if (targetElement) {
            targetElement.focus()
        }
    }

    focusField = (fieldName) => {
        const { id } = this.props

        const selector = `#${id} [name="${fieldName}"]`
        const targetElement = document.querySelector(selector)

        if (targetElement) {
            targetElement.focus()
        }
    }

    setFormikToWindow (formikProps, formId) {
        if (!window.formik) {
            window.formik = {
                [formId]: formikProps
            }
        } else {
            window.formik[formId] = formikProps
        }
    }

    extendSetTouchedAll (formikProps) {
        const { setTouched, initialValues } = formikProps
        return () => {
            return setTouched(Object.keys(initialValues).reduce((acc, key) => {
                acc[key] = true
                return acc
            }, {}))
        }
    }

    extendValidateForm = (validateForm) => {
        return (...args) => {
            this.resetInitialErrorsAndTouched()
            return validateForm(...args)
        }
    }

    getValidateAndTouchAction = (validateForm) => {
        return (ignoreFields) => {
            return validateForm()
                .then(validationErrors => {
                    validationErrors = Object.keys(validationErrors)
                    const filterErrors = (ignoreFields, validationErrors) => validationErrors.filter((i) => ignoreFields.indexOf(i) === -1)
                    return filterErrors(ignoreFields, validationErrors)
                })
        }
    }

    extendOnSubmit = (onSubmit) => {
        return (...args) => {
            this.resetInitialErrorsAndTouched()
            return onSubmit(...args)
        }
    }

    getExtendedFormikProps (formikProps, formId) {
        const { fieldsValidating } = this.state

        return {
            ...formikProps,
            extendedActions: {
                validateField: this.validateField,
                manipulateField: this.manipulateField,
                focusFirst: this.focusFirst,
                focusField: this.focusField,
                scrollToError: this.scrollToError,
                isFieldRequired: this.isFieldRequired,
                validateAndTouch: this.getValidateAndTouchAction(formikProps.validateForm),
            },
            fieldsValidating: fieldsValidating,
            setTouchedAll: this.extendSetTouchedAll(formikProps),
            validateForm: this.extendValidateForm(formikProps.validateForm),
        }
    }

    renderFormikChildren = (props) => {
        const { id, children, preventDirtyNavigationMessage, alwaysDisplayPreventDirtyNavigationMessage, disablePrompt } = this.props
        const { debugVisible } = this.state

        const extendedProps = this.getExtendedFormikProps(props, id)

        this.setFormikToWindow(extendedProps, id)

        const { isSubmitting } = extendedProps

        return (
            <ExtendedFormikContext.Provider value={extendedProps}>
                <ScrollToError
                    formId={id}
                    submitCount={props.submitCount}
                    isValid={props.isValid}
                    scrollToError={this.scrollToError}
                />
                {children(extendedProps)}
                {(debugVisible && !isProductionHost()) && <FormikDebug />}
                <div
                    style={{ display: 'none' }}
                    data-prevent-navigation={
                        (props.dirty || alwaysDisplayPreventDirtyNavigationMessage)
                            ? preventDirtyNavigationMessage
                            : false
                    }
                />
                {!isSubmitting && (props.dirty || alwaysDisplayPreventDirtyNavigationMessage) && !disablePrompt && (
                    <ExtendedFormikPrompt />
                )}
            </ExtendedFormikContext.Provider>
        )
    }

    handleWrapperClick = (e) => {
        if (e.ctrlKey || e.metaKey) { // ctrl for windows, command for mac
            this.toggleDebugView()
        }
    }

    resetInitialErrorsAndTouched = () => {
        if (this.state.initialTouched || this.state.initialErrors) {
            this.setState({
                initialTouched: null,
                initialErrors: null,
            })
        }
    }

    render () {
        const { id, className, initialTouched, initialErrors, onSubmit, ...rest } = this.props

        if (!this.state.renderForm) return null

        return (
// eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div
                id={id}
                className={classNames(
                    'extended-formik',
                    className
                )}
                onClick={!isProductionHost() ? this.handleWrapperClick : null}
            >
                <Formik
                    initialTouched={{
                        ...initialTouched,
                        ...this.state.initialTouched,
                    }}
                    initialErrors={{
                        ...initialErrors,
                        ...this.state.initialErrors,
                    }}
                    onSubmit={this.extendOnSubmit(onSubmit)}
                    {...rest}
                >
                    {(props) => this.renderFormikChildren(props)}
                </Formik>
            </div>
        )
    }
}

ExtendedFormik.propTypes = {
    id: PropTypes.string.isRequired,
    children: PropTypes.func.isRequired,
    validationSchema: PropTypes.object.isRequired,
    initValidation: PropTypes.func,
    disableInitValidationOnFormikReinitialize: PropTypes.bool,
    initValidationValues: PropTypes.object,
    className: PropTypes.string,
    debug: PropTypes.bool,
    autoFocusToField: PropTypes.string,
    autoFocusFirst: PropTypes.bool,
    preventDirtyNavigationMessage: PropTypes.string,
    disablePrompt: PropTypes.bool,
    alwaysDisplayPreventDirtyNavigationMessage: PropTypes.bool,
}

export default ExtendedFormik
